import React, { PureComponent } from 'react';
import moment from 'moment-timezone';
import {
  Platform, StyleSheet, View, Text,
} from 'react-native';
import { translate } from 'react-i18next';
import PropTypes from 'prop-types';
import { BookingsConfig, UserWorkoutSession } from 'fitbeat.gym-core';
import { BookingState } from 'fitbeat.models';
import { verticalScale, moderateScale } from 'react-native-size-matters';
import { Avatar } from '../../components';
import Theme from '../../theme';
import {
  shouldLoadPartnerProfile,
  workoutSessionPairedIdIsSameAsUserUid,
  shouldClearPartnerProfile,
} from '../utils';
import { styles as sharedStyles, getResponsiveStyleSheet } from './Styles';
import { defaultScale } from '../../utils/scaling';

const responsive = getResponsiveStyleSheet();

class UserBookingDisplay extends PureComponent {
  state = {
    partnerProfile: {},
    dummyUpdater: 0,
    avatarSize: Platform.OS == 'web' ? 40 : verticalScale(40, 2),
  };

  async componentDidMount() {
    const { workoutSession, userProfile } = this.props;
    if (!workoutSessionPairedIdIsSameAsUserUid(workoutSession, userProfile)) {
      await this.loadPartnerProfile(workoutSession);
    }
  }

  async componentDidUpdate(prevProps) {
    const { workoutSession, userProfile } = this.props;
    const { partnerProfile } = this.state;
    if (shouldLoadPartnerProfile(prevProps.workoutSession, workoutSession, userProfile, partnerProfile)) {
      await this.loadPartnerProfile(workoutSession);
    } else if (shouldClearPartnerProfile(workoutSession, userProfile, partnerProfile)) {
      this.setState({ partnerProfile: {} });
    }
  }

  componentWillUnmount() {
    this.clearIntervalUpdater();
  }

  clearIntervalUpdater = () => {
    if (this.interval) {
      clearInterval(this.interval);
    }
  };

  createIntervalUpdater = () => {
    if (!this.interval) {
      this.interval = setInterval(this.updateTimeleft, 10000);
    }
  };

  updateTimeleft = () => {
    this.setState({ dummyUpdater: this.state.dummyUpdater + 1 });
  };

  loadPartnerProfile = async (workoutSession) => {
    const { getUserProfileDetails } = this.props;
    try {
      const partnerProfile = await getUserProfileDetails(workoutSession.pairedSessionUserProfileId);
      this.setState({ partnerProfile });
    } catch (error) {
      console.log(error);
    }
  };

  isPairedBooking = () => UserWorkoutSession.hasPairedSession(this.props.workoutSession);

  isPairedBookingExpired = (pairedSession) => pairedSession.bookingState === BookingState.BookingInvite
      && moment.tz(pairedSession.expiresAt, this.props.gymTimezone).isBefore(moment().tz(this.props.gymTimezone));

  renderPartnerProfile = () => {
    const { userProfile, workoutSession } = this.props;

    if (!this.isPairedBooking()
      && workoutSession.pairedSessionUserProfileId === userProfile.uid) {
      return;
    }

    const { t, gymTimezone } = this.props;
    const { pairedSession } = workoutSession;
    const { bookingState, expiresAt } = pairedSession;
    const { partnerProfile, avatarSize } = this.state;

    switch (bookingState) {
      case BookingState.ConfirmedBooking: {
        return this.renderUserBookingWithState(partnerProfile, pairedSession);
      }
      case BookingState.BookingInvite: {
        const isExpired = this.isPairedBookingExpired(pairedSession);

        if (isExpired) {
          this.clearIntervalUpdater();
        }

        const timeLeft = moment(expiresAt).tz(gymTimezone).toNow(true);
        this.createIntervalUpdater();
        const timeLeftText = t('timeLeftToAccept', { timeLeft });
        return this.renderUserBookingWithState(partnerProfile, pairedSession, timeLeftText);
      }
      case BookingState.ProvisionalBooking: {
        let timeLeftText;
        const isExpired = moment.tz(expiresAt, gymTimezone).isBefore(moment().tz(gymTimezone));
        let textColor = Theme.GREY;

        if (isExpired) {
          this.clearIntervalUpdater();
          textColor = Theme.RED;
          timeLeftText = t('invitationExpired');
        } else {
          const timeLeft = moment(expiresAt).tz(gymTimezone).toNow(true);
          this.createIntervalUpdater();

          timeLeftText = t('timeLeftToInvite', { timeLeft });
        }

        return (
          <View style={styles.innerProfileContainer}>
            <Avatar
              avatarId={partnerProfile.uid}
              size={avatarSize}
              iconColor={Theme.GREY_LIGHT}
            />
            <View style={styles.userDetailsContainer}>
              <Text style={[sharedStyles.settingsSubtitle, { color: textColor }]}>
                {timeLeftText}
              </Text>
            </View>
          </View>
        );
      }
      default:
        break;
    }
  };

  getBookingStateText = (bookingState) => {
    const { t, classStarted } = this.props;

    if (classStarted) {
      return t('classStarted');
    }

    switch (BookingState[bookingState]) {
      case 'ConfirmedBooking':
        return t('confirmed');
      case 'BookingInvite':
        return t('invited');
      default:
        break;
    }
  };

  getBookingStateColor = (booking) => {
    const { classStarted } = this.props;

    if (classStarted && booking.checkedIn) {
      return Theme.GREEN;
    }
    if (classStarted && !booking.checkedIn) {
      return Theme.YELLOW;
    }

    switch (BookingState[booking.bookingState]) {
      case 'ConfirmedBooking':
        return Theme.GREEN;
      case 'BookingInvite':
        return Theme.YELLOW;
      default:
        break;
    }
  };

  renderUserBookingWithState = (userProfile, booking, timeLeft) => {
    const bookingStateColor = this.getBookingStateColor(booking);
    const { t, classStarted, gymTimezone } = this.props;
    const { avatarSize } = this.state;
    let timeLeftToCheckIn = '';

    if (classStarted && !booking.checkedIn) {
      const endTimeForCheckIn = moment(booking.startTime).tz(gymTimezone)
        .add(BookingsConfig.maxNumberOfMinutesAfterExerciseForCheckin, 'minutes');
      if (moment(endTimeForCheckIn).tz(gymTimezone).isAfter(moment())) {
        timeLeftToCheckIn = t('timeLeftToCheckIn', { timeLeft: moment(endTimeForCheckIn).tz(gymTimezone).toNow(true) });
        this.createIntervalUpdater();
      } else {
        this.clearIntervalUpdater();
      }
    }
    return (
      <View style={{ flexDirection: 'row' }} key={userProfile.uid}>
        <Avatar
          size={avatarSize}
          iconColor={Theme.GREY_LIGHT}
          avatarId={userProfile.uid}
          customStyle={{
            alignSelf: 'flex-start',
            width: defaultScale(75),
            height: defaultScale(62),
          }}
        />
        <View>
          <Text style={styles.avatarText}>
            {userProfile.firstname}
            {'\n'}
            <Text style={{ color: bookingStateColor }}>{this.getBookingStateText(booking.bookingState)}</Text>
            {'\n'}
            {timeLeft && timeLeft}
            {classStarted && booking.checkedIn && t('checkedIn')}
            {classStarted && !booking.checkedIn && timeLeftToCheckIn}
          </Text>
        </View>
      </View>
    );
  };

  render() {
    const { t, userProfile, workoutSession } = this.props;
    const isPairedBooking = this.isPairedBooking();
    const isPairedBookingExpired = isPairedBooking
      && this.isPairedBookingExpired(workoutSession.pairedSession);

    return (
      <View className='user-booking-display' style={{ marginBottom: defaultScale(15) }}>
        {this.renderUserBookingWithState(userProfile, workoutSession)}
        {isPairedBooking && !isPairedBookingExpired && this.renderPartnerProfile()}
        {isPairedBooking && isPairedBookingExpired && <Text>{t('partnerInvitedExpired')}</Text>}
      </View>
    );
  }
}

UserBookingDisplay.propTypes = {
  t: PropTypes.func.isRequired,
  gymTimezone: PropTypes.string.isRequired,
  userProfile: PropTypes.object.isRequired,
  workoutSession: PropTypes.object.isRequired,
};

const styles = StyleSheet.create({
  innerProfileContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  userDetailsContainer: {
    flex: 1,
    paddingLeft: 5,
  },
  avatarText: {
    ...Theme.textStyle.bodyCopyMed,
    marginLeft: defaultScale(14),
  },
});

export default translate(['bookings'], { wait: true })(UserBookingDisplay);
